














































































































































































































import { Inject, Watch, Component, Prop, Vue } from 'vue-property-decorator'
import { Branch } from '@/api/model/branch'
import { ReferralType } from '@/api/types'

@Component
export default class EditTeamForm extends Vue {
    @Prop({ required: true }) readonly value!: Branch
    @Inject() saveBranch: (redirect: boolean) => Promise<void>
    newEntryReferralType: ReferralType = null
    newEntryZipcode: string = null
    newEntryModal: boolean = false
    copyEntryModal: boolean = false
    copyEntryBranch: Branch = null
    copyEntryReferralType: ReferralType = null
    copyEntryTargetReferralType: ReferralType = null

    canDisableAcceptedReferralType(referralType: string): boolean {
        if (this.getAcceptedReferralTypesValue(referralType) === false) {
            return true
        }

        return this.otherBranchHasAcceptedReferralTypeActive(referralType)
    }

    /**
     * Checks other branches to see if specified referral type is active.
     */
    otherBranchHasAcceptedReferralTypeActive(referralType: string): boolean {
        return this.$currentCompany.branches
            .filter(branch => branch.id !== this.value.id)
            .some(branch => Object.entries(branch.acceptedReferralTypes)
                .some(([type, value]) => type === referralType && value === true))
    }

    /**
     * Fetches value from model. Since the value may not yet be intialized we have an
     * oppertunity here to initialize it before returning.
     */
    getAcceptedReferralTypesValue(referralType: string): boolean {
        if (typeof this.value.acceptedReferralTypes[referralType] === 'undefined') {
            this.value.acceptedReferralTypes[referralType] = false
        }

        return this.value.acceptedReferralTypes[referralType]
    }

    /**
     * Stores new value into model. Prevents disabling when it is the last branch that has
     * a specific referral type active.
     */
    async setAcceptedReferralTypesValue(referralType: string, value: boolean): Promise<void> {
        this.value.acceptedReferralTypes[referralType] = value
    }

    /**
     * Resets the new entry modal to it's original state when the modal closes.
     * Make sure any processing is done before that happens ;)
     */
    @Watch('newEntryModal')
    resetNewEntryFields(opened: boolean): void {
        if (opened === false) {
            this.newEntryReferralType = null
            this.newEntryZipcode = ''
        }
    }

    /**
     * Clear target type after every change to branch/source type, so that we can
     * prevent a submit with incorrect selections.
     */
    @Watch('copyEntryBranch')
    @Watch('copyEntryReferralType')
    resetCopyEntryTarget(): void {
        this.copyEntryTargetReferralType = null
    }

    async deleteZipcode(referralType: string, entry: string): Promise<void> {
        if (typeof this.value.assignedZipcodes[referralType] === 'undefined') {
            return
        }

        if (confirm(this.$t('forms.edit-team-form.delete_question').toString()) === false) {
            return
        }

        this.value.assignedZipcodes[referralType] = this.value.assignedZipcodes[referralType].filter(zipcode => {
            return zipcode !== entry
        })

        this.$nextTick(() => this.$forceUpdate())
        await this.saveBranch(false)
    }

    /**
     * Pushes all new entered zipcodes into the branch listing. Will create type index
     * if needed. Saves updated list to backend.
     */
    async newEntrySubmit(): Promise<void> {
        if (typeof this.value.assignedZipcodes[this.newEntryReferralType] === 'undefined') {
            this.value.assignedZipcodes[this.newEntryReferralType] = []
        }

        this.newEntryZipcodeAsArray
            .forEach(zipcode => this.value.assignedZipcodes[this.newEntryReferralType]
                .push(zipcode))

        this.newEntryModal = false
        this.$nextTick(() => this.$forceUpdate())
        await this.saveBranch(false)
    }

    /**
     * Pushes list of zipcodes into current branch for selected type.
     */
    async copyEntrySubmit(): Promise<void> {
        if (typeof this.value.assignedZipcodes[this.copyEntryTargetReferralType] === 'undefined') {
            this.value.assignedZipcodes[this.copyEntryTargetReferralType] = []
        }

        this.copyEntryTargetList
            .forEach(zipcode => this.value.assignedZipcodes[this.copyEntryTargetReferralType]
                .push(zipcode))

        this.copyEntryModal = false
        this.$nextTick(() => this.$forceUpdate())
        await this.saveBranch(false)
    }

    /**
     * Loops trough every company branch, checking if specified zipcode already
     * exists inside their lists. If found, it will return the offending branch.
     */
    getDuplicate(type: string, zipcode: string): Branch|null {
        const result = this.$currentCompany.branches
            .filter(branch => this.hasDuplicateInBranch(branch, type, zipcode))

        if (result.length === 0) {
            return null
        }

        return result[0]
    }

    /**
     * Checks supplied branch for duplicate zipcode.
     */
    hasDuplicateInBranch(branch: Branch, type: string, zipcode: string): boolean {
        if (typeof branch.assignedZipcodes[type] === 'undefined') {
            return false
        }

        return branch.assignedZipcodes[type].some(entry => entry === zipcode)
    }

    get availableBranches(): Branch[] {
        return this.$currentCompany.getAvailableBranches()
    }

    /**
     * List of available options when choosing a referral type.
     */
    get referralTypeOptions() {
        return this.$currentCompany.handledReferralTypes.map(referralType => ({
            label: this.$t(`referral.type.${referralType}`),
            value: referralType,
        }))
    }

    /**
     * Parses text input into an array of zipcodes.
     */
    get newEntryZipcodeAsArray(): string[] {
        if (this.newEntryZipcode === null) {
            return []
        }

        return this.newEntryZipcode
            .replace(/\D,/g, '') // anything that isnt a comma or numeric value is removed
            .split(',') // split values by comma
            .filter(entry => entry.length > 0) // filter out empty entries
    }

    /**
     * Checks and returns duplicates for entered values when adding new zipcodes.
     * Returns object array containing offending zipcode and the branch it was found in.
     * Filters out empty entries for ease of rendering.
     */
    get newEntryZipcodeDuplicates(): Array<{ zipcode: string, branch: Branch }> {
        return this.newEntryZipcodeAsArray
            .map(zipcode => {
                const branch = this.getDuplicate(this.newEntryReferralType, zipcode)
                return branch === null ? null : {
                    zipcode,
                    branch,
                }

            })
            .filter(entry => entry !== null)
    }

    /**
     * Checks if fields required for adding new zipcodes if valid.
     * Returns true if all entries are 4 characters long and there
     * are no duplicates in the entry.
     */
    get newEntryZipcodeValid(): boolean {
        return (!this.newEntryZipcodeAsArray.some(entry => entry.length !== 4))
            && this.newEntryZipcodeDuplicates.length === 0
    }

    /**
     * Checks if fields required for duplicating a list is valid.
     */
    get copyEntryZipcodeValid(): boolean {
        let valid = true

        if (this.copyEntryReferralType === null || this.copyEntryBranch === null) {
            valid = false
        }

        if (this.copyEntryZipcodeDuplicates.length > 0) {
            return false
        }

        return valid

    }

    /**
     * Populates a list of zipcodes to be copied based on input fields. Used for showing
     * the user which zipcodes will be carried over.
     */
    get copyEntryTargetList(): string[] {
        if (this.copyEntryReferralType === null || this.copyEntryBranch === null) {
            return []
        }

        if (typeof this.copyEntryBranch.assignedZipcodes[this.copyEntryReferralType] === 'undefined') {
            return []
        }

        return this.copyEntryBranch.assignedZipcodes[this.copyEntryReferralType]
    }

    /**
     * Builds a list of offending zipcodes in current branch when copying a list from
     * a different branch.
     */
    get copyEntryZipcodeDuplicates(): Array<{ zipcode: string }> {
        return this.copyEntryTargetList
            .map(zipcode => {
                const isDuplicate = this.hasDuplicateInBranch(this.value, this.copyEntryTargetReferralType, zipcode)
                if (isDuplicate === false) {
                    return null
                }

                return {
                    zipcode,
                }

            })
            .filter(entry => entry !== null)
    }
}
